import React, { createContext, useContext, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './MyTeamsModal.css';

interface ModalTrigger {
  top: number;
  element: HTMLElement | null;
}

const ModalContext = createContext<{
  enabled: boolean;
  trigger: React.MutableRefObject<ModalTrigger | null>;
} | null>(null);

export function MyTeamsModalProvider({ enabled, children }: { enabled: boolean; children: React.ReactNode }) {
  const trigger = useRef<ModalTrigger | null>(null);
  const captureTrigger = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!enabled || !(event.target instanceof Element) || event.target.closest('[data-team-modal-layer]')) return;
    const element = event.target.closest<HTMLElement>('button, a, [role="button"]');
    const rect = element?.getBoundingClientRect();
    trigger.current = {
      top: window.scrollY + (event.detail > 0 ? event.clientY : rect ? rect.top + rect.height / 2 : window.screen.height / 2),
      element,
    };
  };
  return (
    <ModalContext.Provider value={{ enabled, trigger }}>
      <div style={{ display: 'contents' }} onClickCapture={captureTrigger}>{children}</div>
    </ModalContext.Provider>
  );
}

export function useMyTeamsAnchoredModal() {
  return useContext(ModalContext)?.enabled ?? false;
}

export function MyTeamsModal({ children, onClose, busy = false, className = 'my-team-embed', fallbackFocusId = 'myteams-page', anchorBias = 0.5, preferredMaxHeight }: {
  children: React.ReactNode;
  onClose: () => void;
  busy?: boolean;
  className?: string;
  fallbackFocusId?: string;
  // Part de la hauteur placée au-dessus du point de clic (0.5 = centré, plus haut = modal plus haut)
  anchorBias?: number;
  // Hauteur max souhaitée (px), plafonnée à 80 % de l'écran, pour les modals dont le contenu doit être vu d'un coup
  preferredMaxHeight?: number;
}) {
  const context = useContext(ModalContext);
  const layerRef = useRef<HTMLDivElement>(null);
  const enabled = context?.enabled ?? false;
  const triggerRef = context?.trigger;

  useLayoutEffect(() => {
    if (!enabled) return;
    const dialog = layerRef.current?.querySelector<HTMLElement>('.modal-dialog, [data-team-modal-card]');
    if (!dialog) return;
    const trigger = triggerRef?.current;
    const documentHeight = document.documentElement.scrollHeight;
    const anchorTop = trigger?.top ?? window.scrollY + window.screen.height / 2;
    dialog.classList.add('my-team-anchored-dialog');
    dialog.tabIndex = -1;
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    const title = dialog.querySelector('h5, h6');
    if (title?.textContent) dialog.setAttribute('aria-label', title.textContent);
    const position = () => {
      const screenHeight = window.innerWidth > window.screen.width ? window.screen.width : window.screen.height;
      let heightLimit = screenHeight >= window.innerHeight
        ? (window.innerWidth >= 640 ? 220 : 360)
        : Math.min(480, screenHeight * 0.55);
      if (preferredMaxHeight && window.innerWidth < 640) heightLimit = Math.max(heightLimit, Math.min(preferredMaxHeight, screenHeight * 0.8));
      dialog.style.maxHeight = `${Math.max(120, Math.min(heightLimit, window.innerHeight - 24, documentHeight - 24))}px`;
      const height = dialog.getBoundingClientRect().height;
      const viewport = window.visualViewport;
      const visibleTop = window.scrollY + (viewport ? Math.max(0, viewport.offsetTop) : 0);
      const visibleBottom = visibleTop + Math.min(window.innerHeight, viewport?.height ?? window.innerHeight);
      const maxTop = Math.min(documentHeight - height - 12, visibleBottom - height - 12);
      const top = Math.min(anchorTop - height * anchorBias, maxTop);
      dialog.style.setProperty('top', `${Math.max(12, visibleTop + 12 > maxTop ? maxTop : Math.max(top, visibleTop + 12))}px`, 'important');
    };
    position();
    dialog.focus({ preventScroll: true });
    const observer = new ResizeObserver(position);
    observer.observe(dialog);
    window.addEventListener('resize', position);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', position);
      const target = trigger?.element?.isConnected && !trigger.element.matches(':disabled')
        ? trigger.element : document.getElementById(fallbackFocusId);
      target?.focus({ preventScroll: true });
    };
  }, [enabled, triggerRef, fallbackFocusId, anchorBias, preferredMaxHeight]);

  if (!enabled) return <>{children}</>;
  return createPortal(
    <div ref={layerRef} className={`${className} my-team-modal-layer`} data-team-modal-layer
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          if (!busy) onClose();
        }
      }}>
      <div className="my-team-modal-backdrop" aria-hidden="true" onClick={() => !busy && onClose()} />
      {children}
    </div>, document.body,
  );
}